import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import type { SideEffect } from '@bugbug/core/types/utils';
import { ConfirmModal } from '~/components/modals/Modal';
import useModal from '~/hooks/useModal';
import { StepsActions } from '~/modules/steps/steps.redux';
import { selectCurrentStepId } from '~/modules/steps/steps.selectors';
import { useAppSelector } from '~/modules/store';
import { useDeleteGroup } from '~/modules/tests/hooks/useDeleteGroup/useDeleteGroup';
import { useDeleteGroupOnlyFromTestMutation } from '~/modules/tests/tests.api';
import { isInternalServerError } from '~/services/api/utils';

interface DeleteGroupModalProps {
  className?: string;
  group: {
    id: string;
    name: string;
    steps: string[];
  };
  test?: {
    id: string;
    name: string;
  };
  permanent?: boolean;
  onSubmit: SideEffect;
}

const DeleteGroupModal = ({
  className,
  test,
  group,
  permanent = false,
  onSubmit,
}: DeleteGroupModalProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'deleteGroupModal' });
  const dispatch = useDispatch();
  const modal = useModal();
  const currentStepId = useAppSelector(selectCurrentStepId);
  const [deleteOnlyFromTest, deleteOnlyFromTestRequest] = useDeleteGroupOnlyFromTestMutation();
  const [deletePermanently, deletePermanentlyRequest] = useDeleteGroup();

  const handleSuccess = useCallback(() => {
    modal.hide();
    onSubmit?.();

    if (currentStepId && group.steps.includes(currentStepId)) {
      dispatch(StepsActions.resetCurrentStep());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSubmit]);

  const handleRemoveFromTest = async () => {
    if (!test) return;

    await deleteOnlyFromTest({ testId: test.id, groupId: group.id });
    handleSuccess();
  };

  const handleDeletePermanently = async () => {
    await deletePermanently({ id: group.id });
    handleSuccess();
  };

  const title = t('deleteGroupModal.title', 'Do you want to delete "{{name}}"?', {
    name: group.name,
  });

  if (permanent) {
    return (
      <ConfirmModal
        className={className}
        onCancel={modal.hide}
        pending={deletePermanentlyRequest.isLoading}
        hasInternalServerError={isInternalServerError(deletePermanentlyRequest.error)}
        title={title}
        onConfirm={handleDeletePermanently}
      />
    );
  }

  return (
    <ConfirmModal
      className={className}
      onCancel={modal.hide}
      pending={deleteOnlyFromTestRequest.isLoading}
      hasInternalServerError={isInternalServerError(deleteOnlyFromTestRequest.error)}
      title={title}
      onConfirm={handleRemoveFromTest}
    />
  );
};

export default DeleteGroupModal;
